import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { DataButtonSignature } from 'src/app/common/models/data-button-signature';
import { Tooltip } from 'src/app/common/tooltip';
import { Checklist } from 'src/app/components/checklists/checklists';
import { utils } from 'src/app/modules/libs/utils';
import { MessageBanner } from 'src/app/components/messages/services/message-banner';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';
import { WfSignature } from 'src/app/components/workflow/workflow';
import { Option } from 'src/app/components/checklists/checklists';
import { KEResource } from 'src/app/components/kenable/ke';
import { AbsiResource } from 'src/app/components/absi/absi';
import { BaseComponent } from 'src/app/common/base/base.component';

@Component({
  selector: 'cl-base',
  templateUrl: './cl-base.component.html',
  styleUrls: ['./cl-base.component.scss']
})
export class ClBaseComponent extends BaseComponent implements OnDestroy {

  @Input() title?: string | null;
  @Input() textBefore?: string | null;
  @Input() textAfter?: string | null;
  @Input() textAfter2?: string | null;
  @Input() valueString?: string | null;
  @Input() roleIds?: number[] | null;
  @Input() question?: string | null;

  @Input() roleCodes?: string;
  @Input() disabled?: boolean;
  @Input() disableEdit?: boolean;
  @Input() radioDisabled?: boolean;

  @Input() checked?: boolean;
  @Input() color!: string;
  @Input() number?: string | null;
  @Input() checkboxVisible?: boolean = true;
  @Input() warning?: boolean;
  @Input() user?: User | null;
  @Input() moreInfo?: string | null;
  @Input() locationMoreInfo?: string | null;
  @Input() editor?: boolean;
  @Input() builder?: boolean;
  @Input() checklist?: Checklist | KEResource | AbsiResource | null;
  @Input() date?: Date | null;
  @Input() tooltips?: Tooltip[];
  @Input() userNames?: string;
  @Input() suggested?: boolean; // Automatic workflow recommendation shown by the task control.
  @Input() suggestionReason?: string | null; // Explanation shown for an automatic workflow recommendation.

  @Input() value?: boolean;
  @Input() textValue?: string | null;
  @Input() radSCF?: string;

  @Input() showApprove?: boolean;
  @Input() showUnapprove?: boolean;
  @Input() showDisapprove?: boolean;
  @Input() showCancel?: boolean;

  @Input() status!: number;
  @Input() cancelID?: number;
  @Input() taskID?: number;
  @Input() enabled?: boolean;
  @Input() message?: MessageBanner;

  @Input() radioQuestion?: string | null;
  @Input() radioOptions?: Option[];
  @Input() radioSelection?: number;

  @Input() configuration?: string;
  @Input() signature?: WfSignature;

  @Input() dateValue1?: Date;
  @Input() dateValue2?: Date;
  @Input() dateValue3?: Date | null;
  @Input() scheduleResourceId?: number;

  @Input() formId?: number;
  @Input() sectionNumber?: number;
  @Input() sectionId?: number;

  @Input() text?: string | null;
  @Input() label?: string | null;

  @Input() formType?: string | number;
  @Input() serialNo?: string;

  @Input() options?: any[];
  @Input() hilite?: boolean;

  @Output() answered = new EventEmitter<any>();
  @Output() save = new EventEmitter<any>();
  @Output() approve = new EventEmitter<any>();
  @Output() disapprove = new EventEmitter<DataButtonSignature>();
  @Output() unapprove = new EventEmitter<any>();
  @Output() radioSelected = new EventEmitter<any>();
  @Output() changed = new EventEmitter<any>();
  @Output() itemCountChanged = new EventEmitter<number>();

  public privilege = PrivilegeEnum;
  public initials?: string | null;

  valueObjects: any[] = [];

  saveValues(values: any) {
    this.save.emit(utils.JSONstringify(values));
  }

  getValueObjects(valueString?: string | null) {
    let valueObjects = this.utils.JSONparse(valueString);
    if (valueObjects?.[0]?.stringValue) {
      valueObjects = this.utils.JSONparse(valueObjects?.[0]?.stringValue);
      if (typeof valueObjects == 'string')
        valueObjects = this.getValueObjects(valueObjects);
    }
    return valueObjects;
  }

  stripStrong(html: string | null | undefined): string {
    return (html ?? '').replace(/<\/?strong[^>]*>/gi, '').trim();
  }

  flattenNestedB(html: string): string {
    let result = html;
    let prev = '';
    while (prev !== result) {
      prev = result;
      result = result.replace(/(<b[^>]*>)\s*<b[^>]*>/gi, '$1');
      result = result.replace(/<\/b>\s*(<\/b>)/gi, '$1');
    }
    return result;
  }

  /**
   * Returns the `question` string with any UPPERCASE+Number token (e.g. BL04, M202, EZ-002)
   * and slash-separated sequences (e.g. BL04_XS005 / BL04_LW001 or M201/M302)
   * wrapped in bold-blue markup. Strips any <strong> tags first, then flattens nested <b> tags,
   * and finally promotes every plain <b> to <b class="blue">.
   */
  get labelHtml(): string {
    const stripped = this.stripStrong(this.question);
    // Wrap any uppercase-and-number identifiers (possibly with dots, underscores, or hyphens),
    // including slash-separated sequences, in <b> tags for bold-blue rendering.
    const withBold = stripped.replace(
      /\b(?=[A-Z\d]*[A-Z])(?=[A-Z\d]*\d)[A-Z\d]+(?:[._-][A-Z\d]+)*(?:\s*\/\s*[A-Z\d]+(?:[._-][A-Z\d]+)*)*\b/g,
      '<b>$&</b>'
    );
    const flattened = this.flattenNestedB(withBold);
    return flattened.replace(/<b>/gi, '<b class="blue">');
  }

}
